import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { getBackendUrl } from "@/lib/backend";
import { toast } from "sonner";
import "./NipunDashboard.css";

const API = `${getBackendUrl()}/api`;

const DOT_CLASS = { primary: "", green: " green", orange: " orange", red: " red" };
const FILL_CLASS = { green: " green", orange: " orange", red: " red", purple: " purple", "": "" };

const Tag = ({ type, children }) => <span className={`tag${type ? ` ${type}` : ""}`}>{children}</span>;

const KpiCard = ({ metric }) => (
  <div className={`kpi${metric.tone ? ` ${metric.tone}` : ""}`}>
    <small>
      {metric.label} <em>{metric.kpi || ""}</em>
    </small>
    <strong>{metric.value}</strong>
    <span>{metric.hint}</span>
  </div>
);

const NipunDashboard = () => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "executive";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API}/nipun/overview`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load NIPUN dashboard data");
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const gaugeStyle = useMemo(() => {
    const pct = data?.executive?.gauge_pct ?? 0;
    const rate = data?.executive?.nipun_rate ?? 0;
    return {
      background: `radial-gradient(circle at center,#fff 0 53%,transparent 54%),conic-gradient(var(--warning) 0 ${pct}%,rgba(245,158,11,.13) ${pct}% 100%)`,
      title: `${rate}% NIPUN rate`,
    };
  }, [data?.executive?.gauge_pct, data?.executive?.nipun_rate]);

  const donutStyle = useMemo(() => {
    const segments = data?.executive?.donut?.segments;
    if (!segments?.length) return null;
    const colors = { primary: "var(--primary)", green: "var(--success)", orange: "var(--warning)", red: "var(--danger)" };
    const total = segments.reduce((s, seg) => s + seg.count, 0) || 1;
    let cursor = 0;
    const stops = segments.map((seg) => {
      const share = (seg.count / total) * 100;
      const start = cursor;
      cursor += share;
      return `${colors[seg.color] || "var(--primary)"} ${start}% ${cursor}%`;
    });
    return { background: `radial-gradient(circle at center,#fff 0 55%,transparent 56%),conic-gradient(${stops.join(", ")})` };
  }, [data?.executive?.donut?.segments]);

  if (loading) {
    return (
      <div className="content">
        <p style={{ color: "var(--muted)" }}>Loading NIPUN dashboard…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="content">
        <p style={{ color: "var(--danger)" }}>
          NIPUN data unavailable. Ensure NIPUN Data.xlsx and nm_super_schools_feb26.xlsx are in data/excel.
        </p>
      </div>
    );
  }

  const { meta, executive, state, district, block, super_schools: superTab, risk, school, governance } = data;

  return (
    <div className="content">
      <section className="page-head">
        <div>
          <div className="eyebrow">Visual KPI intelligence · No KPI dictionary tables</div>
          <h1>NIPUN & Super Schools Dashboard</h1>
          <p>
            All KPI dictionary items are represented through executive cards, gauges, charts, heatmaps, matrices,
            rankings, and action visuals for official review.
          </p>
        </div>
        <button type="button" className="btn" onClick={() => fetchData(true)} disabled={refreshing}>
          <RefreshCw size={14} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </section>

      {activeTab === "executive" && (
        <section className="view active" id="executive">
          <div className="hero-card">
            <div className="hero-layout">
              <div>
                <div className="gauge-row">
                  <div className="gauge" style={gaugeStyle}>
                    <div>
                      <strong>{executive.nipun_rate}%</strong>
                      <span>NIPUN rate</span>
                    </div>
                  </div>
                  <div className="hero-copy">
                    <h2>{executive.headline}</h2>
                    <p>{executive.summary}</p>
                    <div className="status-row">
                      <span className="status">{executive.chips?.[0]}</span>
                      <span className="status blue">{executive.chips?.[1]}</span>
                      <span className="status red">{executive.chips?.[2]}</span>
                    </div>
                  </div>
                </div>
                <div className="insight">
                  <div className="icon purple">⚡</div>
                  <div>
                    <b>Primary executive action</b>
                    <span>Prioritize zero-score schools and low-performing blocks before expanding excellence interventions.</span>
                  </div>
                </div>
              </div>
              <div className="kpi-grid">
                {executive.metrics?.map((m) => (
                  <KpiCard key={m.label} metric={m} />
                ))}
              </div>
            </div>
          </div>

          <div className="grid-3">
            <div className="visual-card">
              <h3>Super school distribution</h3>
              <p>Share of schools by excellence and support bands.</p>
              <div className="donut" style={donutStyle}>
                <b>{executive.donut?.center_pct}%</b>
              </div>
              <div className="legend">
                {executive.donut?.segments?.map((seg) => (
                  <div key={seg.label} className="legend-row">
                    <span>
                      <i className={`dot${DOT_CLASS[seg.color] || ""}`} />
                      {seg.label}
                    </span>
                    <b>{seg.count.toLocaleString("en-IN")}</b>
                  </div>
                ))}
              </div>
            </div>
            <div className="visual-card">
              <h3>Performance band pyramid</h3>
              <p>KPI-44 to KPI-49 as a visual performance ladder.</p>
              <div className="pyramid">
                {executive.pyramid?.map((row) => (
                  <div key={row.class} className={`pyr ${row.class}`}>
                    {row.label} · {row.count.toLocaleString("en-IN")}
                  </div>
                ))}
              </div>
            </div>
            <div className="visual-card">
              <h3>Strategic KPI health</h3>
              <p>Targets converted into visual health bars.</p>
              <div className="bar-list">
                {executive.health_bars?.map((bar) => (
                  <div key={bar.label} className="hbar-row">
                    <div className="hbar-label">{bar.label}</div>
                    <div className="track">
                      <div className={`fill${FILL_CLASS[bar.fill] || ""}`} style={{ width: `${bar.bar_pct}%` }} />
                    </div>
                    <div className="value">{bar.value}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid-2">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Top NIPUN districts</h3>
                  <p>District achievement ranking visual.</p>
                </div>
                <span className="pill">KPI-16</span>
              </div>
              <div className="bar-list">
                {executive.top_nipun_districts?.map((row) => (
                  <div key={row.district} className="hbar-row">
                    <div className="hbar-label">{row.district}</div>
                    <div className="track">
                      <div className={`fill${row.highlight ? " green" : ""}`} style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="value">{row.nipun_rate}%</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Immediate attention queue</h3>
                  <p>Critical operational signals from KPI-07, KPI-08, KPI-50 and KPI-51.</p>
                </div>
                <span className="pill">Action</span>
              </div>
              <div className="matrix">
                {executive.attention_matrix?.map((item) => (
                  <div key={item.title} className="quad">
                    <h4>{item.title}</h4>
                    <strong>{typeof item.value === "number" ? item.value.toLocaleString("en-IN") : item.value}</strong>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === "state" && (
        <section className="view active" id="state">
          <div className="section-title">
            <div>
              <h2>State performance cockpit</h2>
              <p>State KPIs visualized as gauges, contribution cards, maps and performance bands.</p>
            </div>
            <span className="pill">KPI-11 to KPI-15</span>
          </div>
          <div className="kpi-grid">
            {state.metrics?.map((m) => (
              <KpiCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>District performance map substitute</h3>
                  <p>Heatmap blocks show district health intensity.</p>
                </div>
                <span className="pill">Map view</span>
              </div>
              <div className="heatmap">
                {state.heatmap?.map((cell) => (
                  <div key={cell.district} className={`cell ${cell.cell_class}${cell.highlight ? " highlight" : ""}`}>
                    <b>{cell.district}</b>
                    {cell.nipun_rate}%
                  </div>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>State readiness radar</h3>
                  <p>Composite view without KPI rows.</p>
                </div>
                <span className="pill">Composite</span>
              </div>
              <div className="radar">
                <svg viewBox="0 0 360 320">
                  <g transform="translate(180 160)">
                    <polygon points="0,-125 119,-39 74,101 -74,101 -119,-39" fill="none" stroke="#d7e3f6" />
                    <polygon points="0,-90 86,-28 53,73 -53,73 -86,-28" fill="none" stroke="#d7e3f6" />
                    <polygon points="0,-55 52,-17 32,44 -32,44 -52,-17" fill="none" stroke="#d7e3f6" />
                    <polygon
                      points={state.radar_polygon}
                      fill="rgba(37,99,235,.30)"
                      stroke="#2563eb"
                      strokeWidth="3"
                    />
                    <text x="0" y="-145" textAnchor="middle" fontSize="12" fill="#66758f" fontWeight="850">
                      Achievement
                    </text>
                    <text x="142" y="-43" fontSize="12" fill="#66758f" fontWeight="850">
                      Excellence
                    </text>
                    <text x="88" y="122" fontSize="12" fill="#66758f" fontWeight="850">
                      Coverage
                    </text>
                    <text x="-88" y="122" textAnchor="end" fontSize="12" fill="#66758f" fontWeight="850">
                      Risk control
                    </text>
                    <text x="-142" y="-43" textAnchor="end" fontSize="12" fill="#66758f" fontWeight="850">
                      Readiness
                    </text>
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === "district" && (
        <section className="view active" id="district">
          <div className="section-title">
            <div>
              <h2>District deep dive</h2>
              <p>District KPIs shown as ranking visuals, benchmark gaps and risk panels.</p>
            </div>
            <span className="pill">KPI-16 to KPI-22</span>
          </div>
          <div className="grid-4">
            {district.cards?.map((card) => (
              <div key={card.district + card.subtitle} className="visual-card">
                <h3>{card.district}</h3>
                <p>{card.subtitle}</p>
                <strong style={{ fontSize: 48, color: `var(--${card.tone === "success" ? "success" : card.tone === "danger" ? "danger" : "warning"})` }}>
                  {card.value}
                </strong>
                <div className="track">
                  <div className={`fill${card.tone === "success" ? " green" : card.tone === "danger" ? " red" : " orange"}`} style={{ width: `${card.bar_pct}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="grid-2">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>District excellence ranking</h3>
                  <p>Super School rate by district.</p>
                </div>
                <span className="pill">KPI-17</span>
              </div>
              <div className="bar-list">
                {district.excellence_ranking?.map((row) => (
                  <div key={row.district} className="hbar-row">
                    <div className="hbar-label">{row.district}</div>
                    <div className="track">
                      <div className={`fill${row.highlight ? " green" : ""}`} style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="value">{row.super_rate}%</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>District risk ranking</h3>
                  <p>Low performer concentration by district.</p>
                </div>
                <span className="pill">KPI-18</span>
              </div>
              <div className="bar-list">
                {district.risk_ranking?.map((row) => (
                  <div key={row.district} className="hbar-row">
                    <div className="hbar-label">{row.district}</div>
                    <div className="track">
                      <div className={`fill${row.risk_rate > 85 ? " red" : " orange"}`} style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="value">{row.risk_rate}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === "block" && (
        <section className="view active" id="block">
          <div className="section-title">
            <div>
              <h2>Block & cluster analytics</h2>
              <p>Block, cluster and support KPIs visualized as ranking cards, cluster heatmaps and gap bars.</p>
            </div>
            <span className="pill">KPI-23 to KPI-35</span>
          </div>
          <div className="kpi-grid">
            {block.metrics?.map((m) => (
              <KpiCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Block priority visual</h3>
                  <p>Use this for intervention load and gap to district.</p>
                </div>
                <span className="pill">KPI-28</span>
              </div>
              <div className="leader">
                {block.priorities?.map((row) => (
                  <div key={row.rank} className="leader-row">
                    <div className="rank">{row.rank}</div>
                    <div>
                      <b>{row.title}</b>
                      <span>{row.subtitle}</span>
                    </div>
                    <div className="score">{row.score}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Cluster heatmap</h3>
                  <p>Cluster performance status, replacing cluster KPI list.</p>
                </div>
                <span className="pill">KPI-30–35</span>
              </div>
              <div className="heatmap">
                {block.cluster_heatmap?.map((cell) => (
                  <div key={cell.label} className={`cell ${cell.cell_class}`}>
                    <b>{cell.label}</b>
                    {cell.status}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === "super" && (
        <section className="view active" id="super">
          <div className="section-title">
            <div>
              <h2>Super Schools excellence dashboard</h2>
              <p>Excellence KPIs represented through scorecards, pyramids, district bars and leaderboard visuals.</p>
            </div>
            <span className="pill">KPI-05, 06, 40, 44, 45, 55</span>
          </div>
          <div className="kpi-grid">
            {superTab.metrics?.map((m) => (
              <KpiCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Excellence by district</h3>
                  <p>High-performing district distribution.</p>
                </div>
                <span className="pill">Top districts</span>
              </div>
              <div className="bar-list">
                {superTab.excellence_by_district?.map((row) => (
                  <div key={row.district} className="hbar-row">
                    <div className="hbar-label">{row.district}</div>
                    <div className="track">
                      <div className={`fill${row.highlight ? " green" : ""}`} style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="value">{row.count.toLocaleString("en-IN")}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Excellence ladder</h3>
                  <p>Recognize, replicate, scale.</p>
                </div>
                <span className="pill">Action design</span>
              </div>
              <div className="timeline">
                {superTab.timeline?.map((step) => (
                  <div key={step.step} className="step">
                    <div className="num">{step.step}</div>
                    <div>
                      <b>{step.title}</b>
                      <span>{step.subtitle}</span>
                    </div>
                    <Tag type={step.tag_type}>{step.tag}</Tag>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === "risk" && (
        <section className="view active" id="risk">
          <div className="section-title">
            <div>
              <h2>Risk & intervention dashboard</h2>
              <p>Risk KPIs shown as severity matrix, critical cards, heatmaps and action prioritization.</p>
            </div>
            <span className="pill">KPI-48 to KPI-57</span>
          </div>
          <div className="kpi-grid">
            {risk.metrics?.map((m) => (
              <KpiCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Risk matrix</h3>
                  <p>School count and risk severity are shown as action quadrants.</p>
                </div>
                <span className="pill">Traffic light</span>
              </div>
              <div className="matrix">
                {risk.matrix?.map((item) => (
                  <div
                    key={item.title}
                    className="quad"
                    style={{
                      borderColor:
                        item.tone === "red"
                          ? "rgba(239,68,68,.22)"
                          : item.tone === "orange"
                            ? "rgba(245,158,11,.24)"
                            : "rgba(16,185,129,.20)",
                    }}
                  >
                    <h4>
                      {item.tone === "red" ? "🔴 " : item.tone === "orange" ? "🟠 " : "🟢 "}
                      {item.title}
                    </h4>
                    <strong>{item.value.toLocaleString("en-IN")}</strong>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>District intervention burden</h3>
                  <p>Top risk concentration indicators.</p>
                </div>
                <span className="pill">KPI-53</span>
              </div>
              <div className="bar-list">
                {risk.intervention_burden?.map((row) => (
                  <div key={row.district} className="hbar-row">
                    <div className="hbar-label">{row.district}</div>
                    <div className="track">
                      <div className="fill red" style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="value">{row.burden.toLocaleString("en-IN")}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === "school" && (
        <section className="view active" id="school">
          <div className="section-title">
            <div>
              <h2>School performance drilldown</h2>
              <p>School KPIs shown as scorecards, scatter plot, benchmark gap and peer comparison visuals.</p>
            </div>
            <span className="pill">KPI-36 to KPI-43</span>
          </div>
          <div className="kpi-grid">
            {school.metrics?.map((m) => (
              <KpiCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Student count vs performance</h3>
                  <p>Scatter view for identifying large schools with low outcomes.</p>
                </div>
                <span className="pill">KPI-43</span>
              </div>
              <div className="scatter">
                <div className="axisx" />
                <div className="axisy" />
                {school.scatter?.map((pt, i) => (
                  <span
                    key={i}
                    className={`point${pt.color ? ` ${pt.color}` : ""}`}
                    style={{ left: `${pt.left_pct}%`, bottom: `${pt.bottom_pct}%` }}
                    title={`Enrollment ${pt.enrollment}, Score ${pt.score}%`}
                  />
                ))}
              </div>
              <div className="legend">
                <div className="legend-row">
                  <span>
                    <i className="dot red" />
                    Large enrolment / low score
                  </span>
                  <b>Priority</b>
                </div>
                <div className="legend-row">
                  <span>
                    <i className="dot green" />
                    High score schools
                  </span>
                  <b>Model</b>
                </div>
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>School benchmark visual</h3>
                  <p>Performance bands replace school KPI table.</p>
                </div>
                <span className="pill">Band view</span>
              </div>
              <div className="mini-bars">
                {school.band_bars?.map((bar) => (
                  <div key={bar.label} className="mini-bar">
                    <i
                      style={{
                        height: bar.height,
                        background:
                          bar.color === "danger"
                            ? "var(--danger)"
                            : bar.color === "warning"
                              ? "var(--warning)"
                              : bar.color === "success"
                                ? "var(--success)"
                                : "var(--primary)",
                      }}
                    />
                    {bar.label}
                  </div>
                ))}
              </div>
              <div className="insight">
                <div className="icon">🏫</div>
                <div>
                  <b>Recommended drilldown</b>
                  <span>
                    School → Cluster → Block benchmarking should open with score, rank, band, benchmark gap and
                    suggested action.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === "governance" && (
        <section className="view active" id="governance">
          <div className="section-title">
            <div>
              <h2>Governance & action tracker</h2>
              <p>Governance KPIs are shown as progress cards, action timeline, closure bars and review dashboard visuals.</p>
            </div>
            <span className="pill">Review dashboard</span>
          </div>
          <div className="kpi-grid">
            {governance.metrics?.map((m) => (
              <KpiCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Officer review workflow</h3>
                  <p>Visual governance sequence for district officials.</p>
                </div>
                <span className="pill">Weekly</span>
              </div>
              <div className="timeline">
                {governance.workflow?.map((step) => (
                  <div key={step.step} className="step">
                    <div className="num">{step.step}</div>
                    <div>
                      <b>{step.title}</b>
                      <span>{step.subtitle}</span>
                    </div>
                    <Tag type={step.tag_type}>{step.tag}</Tag>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h3>Action closure dashboard</h3>
                  <p>Use this visual instead of governance KPI list.</p>
                </div>
                <span className="pill">Tracker</span>
              </div>
              <div className="bar-list">
                {governance.closure_bars?.map((bar) => (
                  <div key={bar.label} className="hbar-row">
                    <div className="hbar-label">{bar.label}</div>
                    <div className="track">
                      <div className={`fill${FILL_CLASS[bar.fill] || ""}`} style={{ width: `${bar.bar_pct}%` }} />
                    </div>
                    <div className="value">{bar.value}%</div>
                  </div>
                ))}
              </div>
              <div className="insight">
                <div className="icon">📊</div>
                <div>
                  <b>Official review recommendation</b>
                  <span>
                    Add district/block filters and exportable action lists for CEO, Commissioner, SCERT and Mission
                    NIPUN reviews.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NipunDashboard;
